import React, { useState, useEffect } from 'react';
import { 
  Smartphone, Battery, Shield, Camera, Clock, 
  Truck, Award, MapPin, Phone, Mail, 
  ChevronDown, Star, MessageCircle, ArrowRight,
  Check, Package, Users, ShieldCheck, BadgeCheck,
  Zap, TrendingUp, CheckCircle2, AlertCircle, PlayCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { 
  WHATSAPP_NUMBER,
  HERO_IMAGES,
  STATS,
  SERVICES, 
  MODELS, 
  TESTIMONIALS,
  GARANTIAS,
  URGENCY_MESSAGES,
  FAQ,
  CONTACT_INFO 
} from '../data/mock';

const Home = () => {
  const [selectedModel, setSelectedModel] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [urgencyIndex, setUrgencyIndex] = useState(0);

  useEffect(() => {
    setIsVisible(true);
    
    // Google Ads Conversion Tracking
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', 'AW-17979371888');

    // Rotate urgency messages
    const interval = setInterval(() => {
      setUrgencyIndex((prev) => (prev + 1) % URGENCY_MESSAGES.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const openWhatsApp = (message = 'Olá! Meu iPhone está com problema, podem me ajudar com peça e aplicação?') => {
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    
    // Track conversion
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17979371888/tKC2CMmQs5IcEPDinf1C'
      });
    }
    
    window.open(url, '_blank');
  };

  const handleModelClick = (model) => {
    setSelectedModel(model);
    openWhatsApp(`🔥 Olá! Quero consultar preço de peça + aplicação para ${model.name}. Qual o valor?`);
  };

  const iconMap = {
    Smartphone, Battery, Shield, Camera, Clock,
    Truck, Award, Package, Users, ShieldCheck, BadgeCheck
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* WhatsApp Floating Button with Pulse */}
      <button
        onClick={() => openWhatsApp()}
        className="fixed bottom-8 right-8 z-50 bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white p-5 rounded-full shadow-2xl hover:scale-110 transition-all duration-300 group animate-pulse"
        aria-label="Falar no WhatsApp"
        style={{ boxShadow: '0 10px 40px rgba(37, 211, 102, 0.4)' }}
      >
        <MessageCircle className="w-8 h-8" />
        <span className="absolute right-full mr-4 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-5 py-3 rounded-xl text-sm font-bold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none shadow-xl">
          💬 Resposta em 2 minutos!
        </span>
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-20"></span>
      </button>

      {/* Urgency Bar */}
      <div className="bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 text-white py-3 px-6 text-center font-semibold text-sm md:text-base animate-gradient">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
          <Zap className="w-5 h-5 animate-bounce" />
          <span>{URGENCY_MESSAGES[urgencyIndex]}</span>
        </div>
      </div>

      {/* Header Premium */}
      <header className="bg-white/95 backdrop-blur-xl sticky top-0 z-40 border-b border-gray-200/50 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0071e3] to-[#0077ED] rounded-xl flex items-center justify-center shadow-lg">
              <Smartphone className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-black text-gray-900">
                iP<span className="text-[#0071e3]">a</span>Mobile
              </h1>
              <p className="text-xs text-gray-500 font-medium">Peças Premium Certificadas</p>
            </div>
          </div>
          <Button 
            onClick={() => openWhatsApp()}
            size="lg"
            className="bg-gradient-to-r from-[#0071e3] to-[#0077ED] hover:from-[#0077ED] hover:to-[#0071e3] text-white font-bold px-6 md:px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-xl hover:shadow-2xl"
            style={{ boxShadow: '0 10px 30px rgba(0, 113, 227, 0.3)' }}
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            <span className="hidden sm:inline">Atendimento</span> Imediato
          </Button>
        </div>
      </header>

      {/* Hero Section - Redesenhada com Impacto */}
      <section className="relative overflow-hidden py-20 md:py-32">
        {/* Background com gradiente e padrão */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-blue-50"></div>
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, #0071e3 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              {/* Badge Premium */}
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-5 py-2 rounded-full text-sm font-bold mb-6 shadow-lg animate-pulse">
                <Zap className="w-4 h-4" />
                <span>Resposta Instantânea no WhatsApp</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-black text-gray-900 mb-6 leading-tight">
                Seu iPhone<br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0071e3] to-[#00A8E8]">
                  de Volta ao Normal
                </span><br/>
                em Horas
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-700 mb-4 leading-relaxed font-medium">
                Peças premium com <span className="text-[#0071e3] font-bold">aplicação no local</span> e garantia de até 12 meses.
              </p>
              
              <p className="text-lg text-gray-600 mb-8">
                Mais de <span className="font-bold text-gray-900">10.000 clientes</span> já confiaram na iPaMobile.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => openWhatsApp('🔥 URGENTE! Meu iPhone está com problema, quero resolver AGORA!')}
                  size="lg"
                  className="bg-gradient-to-r from-[#0071e3] to-[#0077ED] hover:from-[#0077ED] hover:to-[#0071e3] text-white font-black px-10 py-8 text-xl rounded-2xl transition-all duration-300 hover:scale-105 group shadow-2xl"
                  style={{ boxShadow: '0 20px 50px rgba(0, 113, 227, 0.4)' }}
                >
                  <MessageCircle className="w-6 h-6 mr-3 group-hover:animate-bounce" />
                  Quero Resolver Meu iPhone
                  <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-2 transition-transform" />
                </Button>
                
                <Button 
                  onClick={() => {
                    document.getElementById('garantias').scrollIntoView({ behavior: 'smooth' });
                  }}
                  size="lg"
                  variant="outline"
                  className="border-3 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white font-bold px-8 py-8 text-lg rounded-2xl transition-all duration-300 group"
                >
                  <ShieldCheck className="w-5 h-5 mr-2" />
                  Ver Garantias
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="mt-10 flex flex-wrap gap-6 items-center">
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full border-2 border-white flex items-center justify-center text-white font-bold text-sm">
                        {String.fromCharCode(64 + i)}
                      </div>
                    ))}
                  </div>
                  <div className="text-sm">
                    <div className="font-bold text-gray-900">10.000+</div>
                    <div className="text-gray-600">iPhones atendidos</div>
                  </div>
                </div>
                
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                  <span className="ml-2 font-bold text-gray-900">4.9/5.0</span>
                </div>
              </div>
            </div>

            {/* Right Image */}
            <div className={`transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
              <div className="relative">
                {/* Floating elements */}
                <div className="absolute -top-6 -right-6 bg-gradient-to-br from-green-400 to-green-600 text-white px-6 py-4 rounded-2xl shadow-2xl z-10 animate-bounce">
                  <div className="font-black text-2xl">12 meses</div>
                  <div className="text-sm font-medium">de garantia</div>
                </div>
                
                <div className="absolute -bottom-6 -left-6 bg-white px-6 py-4 rounded-2xl shadow-2xl z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="font-black text-gray-900">Imediato</div>
                      <div className="text-sm text-gray-600">Atendimento WhatsApp</div>
                    </div>
                  </div>
                </div>

                <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                  <img 
                    src={HERO_IMAGES.main} 
                    alt="Peças Premium iPhone" 
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-0 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-purple-400/10 rounded-full blur-3xl"></div>
      </section>

      {/* Stats Section - Social Proof */}
      <section className="py-16 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(45deg, #0071e3 25%, transparent 25%, transparent 75%, #0071e3 75%, #0071e3), linear-gradient(45deg, #0071e3 25%, transparent 25%, transparent 75%, #0071e3 75%, #0071e3)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 10px 10px'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {STATS.map((stat, index) => {
              const Icon = iconMap[stat.icon];
              return (
                <div 
                  key={index}
                  onClick={() => openWhatsApp()}
                  className="text-center transform hover:scale-110 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0071e3] to-[#00A8E8] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-4xl md:text-5xl font-black text-white mb-2">{stat.number}</div>
                  <div className="text-gray-300 font-medium text-sm md:text-base">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Problem-Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-red-500 text-white px-4 py-2 text-sm font-bold">
              Seu iPhone quebrado = Vida parada
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              iPhone Quebrado é<br/>
              <span className="text-red-500">Tempo e Dinheiro Perdido</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cada dia sem seu iPhone funcionando é um dia de prejuízo, fotos perdidas e estresse.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Problema */}
            <div className="bg-gradient-to-br from-red-50 to-orange-50 p-8 md:p-12 rounded-3xl border-2 border-red-200">
              <div className="w-16 h-16 bg-red-500 rounded-2xl flex items-center justify-center mb-6">
                <AlertCircle className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-6">
                Com peças genéricas:
              </h3>
              <ul className="space-y-4">
                {[
                  'Tela parando de funcionar depois de 1 mês',
                  'Bateria viciada que não segura carga',
                  'Câmera com manchas ou sem foco',
                  'Dinheiro jogado fora',
                  'Seu iPhone ficando cada vez pior'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white font-bold text-sm">✕</span>
                    </div>
                    <span className="text-gray-700 font-medium text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Solução */}
            <div className="bg-gradient-to-br from-green-50 to-blue-50 p-8 md:p-12 rounded-3xl border-2 border-green-200 shadow-xl">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                <CheckCircle2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-6">
                Com a iPaMobile:
              </h3>
              <ul className="space-y-4 mb-8">
                {[
                  'Garantia de até 12 meses na aplicação',
                  'Peças premium de alta qualidade',
                  'Atendimento imediato no WhatsApp',
                  'Seu iPhone voltando como novo',
                  'Equipe especializada em Apple'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white font-bold" />
                    </div>
                    <span className="text-gray-700 font-medium text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => openWhatsApp('Meu iPhone está com problema! Quero resolver com garantia!')}
                size="lg"
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-500 text-white font-black py-6 text-lg rounded-xl transition-all duration-300 hover:scale-105 shadow-xl"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Quero Resolver Meu iPhone
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Premium */}
      <section id="servicos" className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-[#0071e3] text-white px-4 py-2 text-sm font-bold">
              Peças + Aplicação Premium
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              A Qualidade que Seu iPhone Merece
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cada aplicação é realizada com peças premium e equipe especializada.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {SERVICES.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card 
                  key={service.id}
                  className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-2 bg-white overflow-hidden group relative"
                  onClick={() => openWhatsApp(`Quero saber mais sobre ${service.title}!`)}
                >
                  {service.popular && (
                    <div className="absolute top-4 right-4 z-10">
                      <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 text-xs font-bold shadow-lg">
                        🔥 MAIS VENDIDO
                      </Badge>
                    </div>
                  )}
                  
                  <CardContent className="p-0">
                    {service.image && (
                      <div className="relative h-64 overflow-hidden">
                        <img 
                          src={service.image} 
                          alt={service.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4">
                          <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-xl">
                            <Icon className="w-7 h-7 text-[#0071e3]" />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="p-8">
                      <h3 className="text-2xl font-black text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 mb-6 text-lg">{service.description}</p>
                      
                      {service.features && (
                        <ul className="space-y-3 mb-6">
                          {service.features.map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-gray-700">
                              <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                              <span className="font-medium">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      <Button 
                        className="w-full bg-gradient-to-r from-[#0071e3] to-[#0077ED] hover:from-[#0077ED] hover:to-[#0071e3] text-white font-bold py-4 rounded-xl transition-all duration-300 group-hover:shadow-xl"
                      >
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Consultar Preço Agora
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Models Table - Redesigned */}
      <section id="modelos" className="py-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'
        }}></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 text-sm font-bold">
              Preços Competitivos
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Cotação Instantânea por Modelo
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Clique no botão do seu modelo e receba cotação completa em menos de 2 minutos
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-[#0071e3] to-[#0077ED] text-white">
                    <th className="px-6 py-5 text-left font-black text-lg">Modelo</th>
                    <th className="px-6 py-5 text-left font-black text-lg">Display</th>
                    <th className="px-6 py-5 text-left font-black text-lg">Bateria</th>
                    <th className="px-6 py-5 text-left font-black text-lg">Vidro Traseiro</th>
                    <th className="px-6 py-5 text-center font-black text-lg">Cotação</th>
                  </tr>
                </thead>
                <tbody>
                  {MODELS.map((model, index) => (
                    <tr 
                      key={model.id}
                      className={`border-b border-gray-100 hover:bg-gradient-to-r hover:from-blue-50 hover:to-transparent transition-all duration-300 ${
                        model.popular ? 'bg-blue-50/50' : 'bg-white'
                      }`}
                    >
                      <td className="px-6 py-6 font-black text-gray-900 text-lg">
                        <div className="flex items-center gap-3">
                          {model.name}
                          {model.popular && (
                            <Badge className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs px-2 py-1 font-bold">
                              🔥 TOP
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-6 text-gray-700 font-bold text-lg">{model.display}</td>
                      <td className="px-6 py-6 text-gray-700 font-bold text-lg">{model.battery}</td>
                      <td className="px-6 py-6 text-gray-700 font-bold text-lg">{model.backGlass}</td>
                      <td className="px-6 py-6 text-center">
                        <Button
                          onClick={() => handleModelClick(model)}
                          className="bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#25D366] text-white font-bold px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg"
                        >
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Orçamento
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-t-4 border-green-500 p-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-xl">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h4 className="font-black text-gray-900 text-xl mb-1">Garantia de Até 12 Meses</h4>
                    <p className="text-gray-600 font-medium">Nas aplicações realizadas conosco</p>
                  </div>
                </div>
                <Button 
                  onClick={() => openWhatsApp('Quero saber TUDO sobre a garantia das aplicações!')}
                  size="lg"
                  className="bg-gray-900 hover:bg-gray-800 text-white font-black px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  <ShieldCheck className="w-5 h-5 mr-2" />
                  Detalhes da Garantia
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Garantias Section */}
      <section id="garantias" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-green-500 text-white px-4 py-2 text-sm font-bold">
              Sua Tranquilidade é Nossa Prioridade
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Por Que Somos Diferentes?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Não é só sobre vender peças. É sobre construir uma parceria de longo prazo.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {GARANTIAS.map((item, index) => {
              const Icon = iconMap[item.icon];
              return (
                <Card 
                  key={index}
                  onClick={() => openWhatsApp('Quero saber mais sobre as garantias da iPaMobile!')}
                  className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-gradient-to-br from-white to-gray-50 p-8 text-center cursor-pointer group"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-[#0071e3] to-[#00A8E8] rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-xl transform hover:rotate-12 transition-transform duration-300">
                    <Icon className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="font-black text-xl text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 font-medium leading-relaxed">{item.description}</p>
                </Card>
              );
            })}
          </div>

          {/* CTA Central */}
          <div className="bg-gradient-to-r from-[#0071e3] to-[#0077ED] rounded-3xl p-12 text-center shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}></div>
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-black text-white mb-6">
                Pronto para Seu iPhone Voltar a Funcionar Perfeitamente?
              </h3>
              <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                Junte-se a mais de 10.000 clientes satisfeitos com nosso atendimento
              </p>
              <Button 
                onClick={() => openWhatsApp('SIM! Quero resolver meu iPhone com garantia de até 12 meses!')}
                size="lg"
                className="bg-white text-[#0071e3] hover:bg-gray-100 font-black px-12 py-7 text-xl rounded-2xl transition-all duration-300 hover:scale-105 shadow-2xl"
              >
                <MessageCircle className="w-6 h-6 mr-3" />
                Quero Resolver Meu iPhone
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Redesigned */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-yellow-500 text-gray-900 px-4 py-2 text-sm font-bold">
              ⭐ Avaliação 4.9/5.0
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              O Que Nossos Clientes Dizem
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Depoimentos reais de quem confiou na iPaMobile
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <Card 
                key={testimonial.id}
                onClick={() => openWhatsApp('Vi os depoimentos e quero saber mais sobre a iPaMobile!')}
                className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-white overflow-hidden cursor-pointer"
              >
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-6">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  
                  <p className="text-gray-700 mb-6 text-lg leading-relaxed font-medium italic">
                    "{testimonial.text}"
                  </p>
                  
                  <div className="flex items-center gap-4 pt-6 border-t border-gray-100">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center text-white font-black text-xl shadow-lg">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <div className="font-black text-gray-900 text-lg">{testimonial.name}</div>
                      <div className="text-gray-600 text-sm font-medium">{testimonial.role}</div>
                      <div className="text-gray-400 text-xs mt-1">{testimonial.date}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => openWhatsApp('Quero ver MAIS depoimentos de clientes satisfeitos!')}
              variant="outline"
              size="lg"
              className="border-2 border-gray-300 text-gray-900 hover:border-[#0071e3] hover:text-[#0071e3] hover:bg-blue-50 font-bold px-8 py-4 rounded-xl transition-all duration-300"
            >
              <Star className="w-5 h-5 mr-2" />
              Ver Mais Depoimentos
            </Button>
          </div>
        </div>
      </section>

      {/* Mapa - Google Maps Embed */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-500 text-white px-4 py-2 text-sm font-bold">
              📍 Nossa Localização
            </Badge>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Venha nos Visitar
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Estamos em Chácara Santo Antônio, São Paulo - SP. Atendimento no local com peças premium e aplicação profissional.
            </p>
          </div>

          <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-gray-100">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3656.5!2d-46.701966!3d-23.6331841!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce5086964dfbc7%3A0xfcbaddd93a042003!2siPhoneApple!5e0!3m2!1spt-BR!2sbr!4v1700000000000"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="iPhoneApple Localização"
            ></iframe>
          </div>

          <p className="text-center text-sm text-gray-500 mt-6">
            R. Alexandre Dumas, 1102 - Conjunto 08 - Térreo — Chácara Santo Antônio, São Paulo - SP
          </p>

          <div className="text-center mt-8">
            <a
              href="https://www.google.com/maps/place/iPhoneApple/@-23.6331841,-46.701966,15z/data=!3m1!4b1!4m6!3m5!1s0x94ce5086964dfbc7:0xfcbaddd93a042003!8m2!3d-23.6331841!4d-46.701966!16s%2Fg%2F11gzcvkxt"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#0071e3] to-[#0077ED] hover:from-[#0077ED] hover:to-[#0071e3] text-white font-bold px-8 py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <MapPin className="w-5 h-5" />
              Abrir no Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* FAQ - Mantido mas melhorado */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
              Perguntas Frequentes
            </h2>
            <p className="text-xl text-gray-600">
              Tudo o que você precisa saber antes de fazer seu pedido
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {FAQ.map((item) => (
              <AccordionItem 
                key={item.id} 
                value={`item-${item.id}`}
                className="bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-2xl px-6 shadow-sm hover:shadow-xl transition-all duration-300 hover:border-[#0071e3]"
              >
                <AccordionTrigger className="text-left font-black text-lg text-gray-900 hover:text-[#0071e3] transition-colors py-6">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 text-base leading-relaxed pb-6 font-medium">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          
          <div className="mt-16 text-center bg-gradient-to-br from-blue-50 to-purple-50 p-10 rounded-3xl border-2 border-blue-200">
            <h3 className="text-2xl font-black text-gray-900 mb-4">
              Ainda tem dúvidas?
            </h3>
            <p className="text-gray-600 mb-6 text-lg">
              Nossa equipe responde em menos de 2 minutos no WhatsApp
            </p>
            <Button 
              onClick={() => openWhatsApp('Tenho algumas dúvidas sobre as peças. Podem me ajudar?')}
              size="lg"
              className="bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#25D366] text-white font-black px-10 py-6 text-lg rounded-2xl transition-all duration-300 hover:scale-105 shadow-2xl"
            >
              <MessageCircle className="w-6 h-6 mr-3" />
              Tirar Minhas Dúvidas AGORA
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA - Ultra Impactante */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-[#0071e3] to-gray-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzIuMjEgMCA0IDEuNzkgNCA0cy0xLjc5IDQtNCA0LTQtMS43OS00LTQgMS43OS00IDQtNHptMC0yYy0zLjMxIDAtNiAyLjY5LTYgNnMyLjY5IDYgNiA2IDYtMi42OSA2LTYtMi42OS02LTYtNnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>
        
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-full text-sm font-bold mb-8 shadow-xl animate-pulse">
            <Zap className="w-5 h-5" />
            <span>Atendimento em Tempo Real no WhatsApp</span>
          </div>
          
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8 leading-tight">
            Não Perca Mais<br/>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
              Dinheiro e Tempo
            </span>
          </h2>
          
          <p className="text-2xl md:text-3xl text-blue-100 mb-6 font-bold">
            Atendimento Imediato. Resposta em Segundos.
          </p>
          
          <p className="text-xl text-blue-200 mb-12 max-w-3xl mx-auto">
            Sua resposta em até 2 minutos no WhatsApp.<br/>
            Tire seu iPhone do problema hoje.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Button 
              onClick={() => openWhatsApp('🚨 URGENTE! Meu iPhone quebrou, preciso resolver AGORA!')}
              size="lg"
              className="bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#25D366] text-white font-black px-16 py-8 text-2xl rounded-2xl transition-all duration-300 hover:scale-110 shadow-2xl group"
              style={{ boxShadow: '0 20px 60px rgba(37, 211, 102, 0.5)' }}
            >
              <MessageCircle className="w-8 h-8 mr-4 group-hover:animate-bounce" />
              QUERO MEU ORÇAMENTO AGORA
              <ArrowRight className="w-8 h-8 ml-4 group-hover:translate-x-2 transition-transform" />
            </Button>
          </div>
          
          <div className="flex flex-wrap justify-center gap-8 text-white/90">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Check className="w-6 h-6" />
              </div>
              <span className="text-lg font-bold">Resposta em 2min</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Check className="w-6 h-6" />
              </div>
              <span className="text-lg font-bold">Garantia 12 Meses</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Check className="w-6 h-6" />
              </div>
              <span className="text-lg font-bold">Pronta Entrega</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Premium */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-[#0071e3] to-[#0077ED] rounded-xl flex items-center justify-center shadow-lg">
                  <Smartphone className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-black">
                  iP<span className="text-[#0071e3]">a</span>Mobile
                </h3>
              </div>
              <p className="text-gray-400 leading-relaxed mb-6 font-medium">
                Peças premium para iPhone, iPad, Apple Watch e AirPods com aplicação no local. Qualidade padrão original e garantia estendida.
              </p>
              <div className="flex gap-1 mb-2">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <p className="text-sm text-gray-400">10.000+ clientes satisfeitos</p>
            </div>
            
            <div>
              <h4 className="font-black text-xl mb-6">Contato Rápido</h4>
              <div className="space-y-4 text-gray-400">
                <div 
                  className="flex items-start gap-3 hover:text-[#25D366] transition-colors cursor-pointer group" 
                  onClick={() => openWhatsApp()}
                >
                  <div className="w-10 h-10 bg-[#25D366]/10 rounded-lg flex items-center justify-center group-hover:bg-[#25D366]/20 transition-colors">
                    <Phone className="w-5 h-5 text-[#25D366]" />
                  </div>
                  <div>
                    <div className="font-bold text-white">(11) 98941-7725</div>
                    <div className="text-sm">Atendimento Imediato</div>
                  </div>
                </div>
                <a 
                  href={`mailto:${CONTACT_INFO.email}`}
                  className="flex items-start gap-3 hover:text-blue-300 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                    <Mail className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <div className="font-bold text-white">{CONTACT_INFO.email}</div>
                    <div className="text-sm">Email comercial</div>
                  </div>
                </a>
                <a
                  href="https://www.google.com/maps/place/iPhoneApple/@-23.6331841,-46.701966,15z/data=!3m1!4b1!4m6!3m5!1s0x94ce5086964dfbc7:0xfcbaddd93a042003!8m2!3d-23.6331841!4d-46.701966!16s%2Fg%2F11gzcvkxt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 hover:text-purple-300 transition-colors cursor-pointer group"
                >
                  <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                    <MapPin className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <div className="font-bold text-white">São Paulo - SP</div>
                    <div className="text-sm">{CONTACT_INFO.address}</div>
                    <div className="text-xs text-purple-400 mt-1">📍 Abrir no Maps</div>
                  </div>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-black text-xl mb-6">Horário</h4>
              <div className="space-y-3 text-gray-400 mb-8">
                <div className="flex justify-between items-center pb-3 border-b border-gray-800">
                  <span className="font-medium">Segunda - Sexta</span>
                  <span className="font-bold text-white">9h - 18h</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b border-gray-800">
                  <span className="font-medium">Sábado</span>
                  <span className="font-bold text-white">9h - 13h</span>
                </div>
                <div className="bg-gradient-to-r from-green-500/10 to-green-600/10 border border-green-500/20 rounded-xl p-4 mt-4">
                  <div className="flex items-center gap-2 text-[#25D366] font-bold mb-1">
                    <MessageCircle className="w-5 h-5" />
                    <span>WhatsApp 24/7</span>
                  </div>
                  <p className="text-sm text-gray-400">Atendimento automático fora do horário</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-gray-400 mb-3 font-medium">© 2026 iPaMobile. Todos os direitos reservados.</p>
            <p className="text-xs text-gray-500 max-w-3xl mx-auto leading-relaxed">
              A iPaMobile atua exclusivamente na venda e fornecimento de peças e componentes de padrão original e qualidade premium para iPhone, iPad, Apple Watch e AirPods, com aplicação realizada em seu ponto de atendimento. Não somos afiliados à Apple Inc. iPhone, iPad, Apple Watch e AirPods são marcas registradas da Apple Inc.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
